/**
 * Live Badges: artwork and pill colours for the badges shown in live chat and on the Top Fans
 * leaderboard: Top Fans (12 + the 3 chat-pill icons), Daily Streak (8 awards + the base pill icon)
 * and PingTop Level (17 ranks, colours only; the rank art stays on the Live Quest page).
 *
 * Each badge is one inactive wallet product (CategoryKey LIVE_BADGE, never sold): an optional
 * `image` media and optional `Metadata.PillColors` { start, end, text }. tezda-ws
 * (`services/liveBadges.ts`) and tezda-live (`live.top-fan-badges.ts`) read them by ProductKey:
 * change the keys together.
 */

export const LIVE_BADGE_CATEGORY_KEY = 'LIVE_BADGE';
export const LIVE_BADGE_PRODUCT_TYPE = 'utility';
export const LIVE_BADGE_CONTENT_TYPES = ['image/png', 'image/webp'];

/** Pill background gradient and label colour, `#RRGGBB`. */
export type PillColors = { start: string; end: string; text: string };

export type LiveBadgeSection = 'top-fan' | 'daily-streak' | 'pingtop-level';

/**
 * - pill_icon: square icon drawn at 14 px in the chat pill.
 * - badge: a full badge (leaderboard rank or streak award), also drawn small in the pill.
 * - colors_only: no upload here, only pill colours (P-Level ranks).
 */
export type LiveBadgeKind = 'pill_icon' | 'badge' | 'colors_only';

export type LiveBadgeSlot = {
  productKey: string;
  section: LiveBadgeSection;
  group: string;
  kind: LiveBadgeKind;
  title: string;
  description: string;
  /** The built-in artwork the app shows today, previewed until a file is uploaded. */
  defaultUrl?: string;
  /** Live Quest rank id whose artwork is previewed (colours_only P-Level slots). */
  rankId?: string;
  /** Whether this badge has pill colours. */
  colors: boolean;
  /** The colours the app uses today, shown until custom ones are saved. */
  defaultColors?: PillColors;
  /** Example label the app draws next to the icon, for the preview. */
  previewLabel: string;
  /** What the app shows while nothing is uploaded. */
  fallback: string;
  order: number;
};

/** Cards on a page are shown in these groups, in this order. */
export type LiveBadgeGroup = {
  id: string;
  section: LiveBadgeSection;
  title: string;
  intro: string;
};

export const LIVE_BADGE_SECTIONS: Record<LiveBadgeSection, { title: string; path: string; intro: string }> = {
  'top-fan': {
    title: 'Top Fan Badges',
    path: '/live-badges/top-fan',
    intro:
      'Top fan artwork and pill colours: the Top1 / Top2 / Top3 pill in live chat, and the rank badges on the Top Fans leaderboard (Live, Daily, Weekly, Monthly).',
  },
  'daily-streak': {
    title: 'Daily Streak Badges',
    path: '/live-badges/daily-streak',
    intro:
      "The streak pill before a viewer's name in live chat shows the award they have reached (The Seed at 3 days ... Eternal at 730) with that award's colours; below 3 days, the base icon.",
  },
  'pingtop-level': {
    title: 'PingTop Level Badges',
    path: '/live-badges/pingtop-level',
    intro:
      "Pill colours for the 17 PingTop Level ranks, shown before a viewer's name in live chat. The rank artwork is uploaded on the Live Quest page (Permanent ranks).",
  },
};

export const LIVE_BADGE_MAX_BYTES: Record<LiveBadgeKind, number> = {
  pill_icon: 128 * 1024,
  badge: 256 * 1024,
  colors_only: 0,
};

export const LIVE_BADGE_UPLOAD_RULES: Record<LiveBadgeKind, string> = {
  pill_icon: 'Square transparent PNG or WebP, 64–128 px, 128 KB max, no text: the app draws the label itself.',
  badge: 'Transparent PNG or WebP, about 128–256 px tall, 256 KB max. Keep the same shape as the badge it replaces.',
  colors_only: 'Colours only. Upload rank artwork on the Live Quest page.',
};

/** The app's colours today (ZEN_Core live chat pills), shown until custom ones are saved. */
export const TOP_FAN_DEFAULT_COLORS: PillColors = { start: '#FF8A3D', end: '#FF2D46', text: '#FFFFFF' };
export const STREAK_DEFAULT_COLORS: PillColors = { start: '#FFE0A3', end: '#FF9F2E', text: '#6B2C00' };

const BADGE_CDN = 'https://cdn2.pingtop.co/assets/live/badges';

/**
 * Top Fans leaderboard periods: the four tabs of the Top Fans sheet (tezda-live `getTopFans`)
 * and the badge on a top fan's join banner (tezda-ws, daily/weekly/monthly). `names` are the
 * badge titles, rank 1-3; `files` are the built-in artwork on the CDN (the fallback).
 */
export const TOP_FANS_PERIODS = [
  { period: 'live', title: 'Top Fans – Live', when: 'this live session', names: ['Live MVP', 'Live Elite', 'Live Star'], files: ['top-fans-live/live-mvp', 'top-fans-live/live-elite', 'top-fans-live/live-star'] },
  { period: 'daily', title: 'Top Fans – Daily', when: 'the last 24 hours', names: ['Daily Star', 'Daily Hero', 'Daily Pro'], files: ['top-fans-daily/daily-star', 'top-fans-daily/daily-hero', 'top-fans-daily/daily-pro'] },
  { period: 'weekly', title: 'Top Fans – Weekly', when: 'the last 7 days', names: ['Weekly King', 'Weekly Legend', 'Weekly Warrior'], files: ['top-fans-weekly/weekly-king', 'top-fans-weekly/weekly-legend', 'top-fans-weekly/weekly-warrior'] },
  { period: 'monthly', title: 'Top Fans – Monthly', when: 'the last 30 days', names: ['Supreme Fan', 'Titan', 'Elite'], files: ['top-fans-monthly/supreme-fan', 'top-fans-monthly/titan', 'top-fans-monthly/elite'] },
] as const;

/** Daily check-in streak awards (tezda-wallet updateDailyStreak), with the art awarded today. */
export const STREAK_AWARDS = [
  { days: 3, title: 'The Seed', url: 'https://cdn2.pingtop.co/assets/badges/The%20Seed-%203%20day.png' },
  { days: 7, title: 'Rising Star', url: 'https://cdn2.pingtop.co/assets/badges/Rising%20Star-%2010%20day.png' },
  { days: 14, title: 'Champion', url: 'https://cdn2.pingtop.co/assets/badges/Champion%20-%2030%20day.png' },
  { days: 30, title: 'Legend', url: 'https://cdn2.pingtop.co/assets/badges/Legend-%20100%20day.png' },
  { days: 90, title: 'Hall of Fame', url: 'https://cdn2.pingtop.co/assets/badges/Hall%20of%20Fame-%20200%20day.png' },
  { days: 180, title: 'Immortal', url: 'https://cdn2.pingtop.co/assets/badges/Hall%20of%20Fame-%20200%20day-1.png' },
  { days: 365, title: 'Mythic', url: 'https://cdn2.pingtop.co/assets/badges/Hall%20of%20Fame-%20200%20day-2.png' },
  { days: 730, title: 'Eternal', url: 'https://cdn2.pingtop.co/assets/badges/Hall%20of%20Fame-%20200%20day-3.png' },
] as const;

/**
 * PingTop Level ranks (tezda-ws `services/liveQuest/catalog.ts` ACCOUNT_RANKS) with the app's
 * pill palette today (ZEN_Core PingTopRankPill): [id, title, first level, start, end, text].
 */
export const PINGTOP_LEVEL_RANKS: Array<[string, string, number, string, string, string]> = [
  ['newcomer', 'Newcomer', 1, '#E6EBFF', '#A9B7E8', '#27345C'],
  ['explorer', 'Explorer', 10, '#DCC7FF', '#9561F1', '#36126E'],
  ['rising_star', 'Rising Star', 20, '#FFE3A2', '#FF9A43', '#713400'],
  ['challenger', 'Challenger', 30, '#C8E0FF', '#377DFF', '#103C83'],
  ['achiever', 'Achiever', 40, '#D0FFCB', '#40C78B', '#07573A'],
  ['elite', 'Elite', 50, '#B6D8FF', '#3870E8', '#122C7A'],
  ['master', 'Master', 60, '#F4D0FF', '#A94BE8', '#581382'],
  ['grandmaster', 'Grandmaster', 70, '#FFD3B4', '#E95036', '#7A1E17'],
  ['legend', 'Legend', 80, '#FFF0B0', '#DCA23B', '#694000'],
  ['mythic', 'Mythic', 90, '#F9C3FF', '#A738D6', '#5B137C'],
  ['champion', 'Champion', 100, '#D4EBFF', '#4A94E9', '#123E79'],
  ['titan', 'Titan', 150, '#C9F2FF', '#3994F0', '#10447C'],
  ['immortal', 'Immortal', 200, '#FFD2AE', '#E64036', '#7B1D13'],
  ['ascendant', 'Ascendant', 300, '#E9D8FF', '#8051CC', '#41217A'],
  ['celestial', 'Celestial', 400, '#FFF2B8', '#E4A72E', '#704500'],
  ['eternal', 'Eternal', 500, '#C6D8FF', '#3953CC', '#192C79'],
  ['infinite_legend', 'Infinite Legend', 1000, '#FFE7B0', '#D779CE', '#57224F'],
];

export const LIVE_BADGE_GROUPS: LiveBadgeGroup[] = [
  {
    id: 'chat-pill',
    section: 'top-fan',
    title: 'Chat pill icon',
    intro:
      "Small icon in the Top1 / Top2 / Top3 pill before a top fan's name in live chat. The pill's colours come from that rank's Top Fans – Monthly badge below (chat top fans are the 30-day top 3).",
  },
  ...TOP_FANS_PERIODS.map(({ period, title, when }) => ({
    id: `top-fans-${period}`,
    section: 'top-fan' as const,
    title,
    intro: `Badges on the ${title.split('– ')[1]} tab of the Top Fans leaderboard: the top 3 gifters of ${when}.${
      period === 'live' ? '' : " Also shown on a top fan's join banner."
    }${period === 'monthly' ? ' Their colours are the chat Top1 / Top2 / Top3 pill colours.' : ''}`,
  })),
  {
    id: 'daily-streak-base',
    section: 'daily-streak',
    title: 'Base streak pill',
    intro: 'Shown below the first award (1–2 days). Its colours are also used by any award without its own colours.',
  },
  {
    id: 'daily-streak-awards',
    section: 'daily-streak',
    title: 'Streak awards',
    intro: 'The 8 awards from the Awards Collection. The pill shows the highest one the viewer has reached.',
  },
  { id: 'pingtop-level-ranks', section: 'pingtop-level', title: 'Ranks', intro: 'One colour set per rank.' },
];

export const LIVE_BADGE_SLOTS: LiveBadgeSlot[] = [
  ...([1, 2, 3] as const).map((rank) => ({
    productKey: `LIVE_BADGE_TOP_FAN_${rank}`,
    section: 'top-fan' as const,
    group: 'chat-pill',
    kind: 'pill_icon' as const,
    colors: false,
    title: `Top Fan #${rank}`,
    description: `Pill icon for this host's #${rank} top fan.`,
    previewLabel: `Top${rank}`,
    fallback: 'Top Fans Live thumbnail, then the crown',
    order: rank,
  })),
  ...TOP_FANS_PERIODS.flatMap(({ period, names, files }, periodIndex) =>
    names.map((name, index) => ({
      productKey: `LIVE_BADGE_TOP_FANS_${period.toUpperCase()}_${index + 1}`,
      section: 'top-fan' as const,
      group: `top-fans-${period}`,
      kind: 'badge' as const,
      defaultUrl: `${BADGE_CDN}/${files[index]}.png`,
      colors: true,
      defaultColors: TOP_FAN_DEFAULT_COLORS,
      title: `#${index + 1} ${name}`,
      description: `Rank ${index + 1} badge.`,
      previewLabel: `Top${index + 1}`,
      fallback: 'the built-in badge shown here',
      order: 20 + periodIndex * 10 + index,
    }))
  ),
  {
    productKey: 'LIVE_BADGE_DAILY_STREAK',
    section: 'daily-streak',
    group: 'daily-streak-base',
    kind: 'pill_icon',
    colors: true,
    defaultColors: STREAK_DEFAULT_COLORS,
    title: 'Daily Streak',
    description: 'Viewers on a 1–2 day streak.',
    previewLabel: '2',
    fallback: 'the fire icon',
    order: 10,
  },
  ...STREAK_AWARDS.map(({ days, title, url }, index) => ({
    productKey: `LIVE_BADGE_STREAK_${days}`,
    section: 'daily-streak' as const,
    group: 'daily-streak-awards',
    kind: 'badge' as const,
    defaultUrl: url,
    colors: true,
    defaultColors: STREAK_DEFAULT_COLORS,
    title: `${days} days · ${title}`,
    description: `Viewers on a streak of ${days} days or more${index < STREAK_AWARDS.length - 1 ? `, up to ${STREAK_AWARDS[index + 1].days - 1}` : ''}.`,
    previewLabel: String(days),
    fallback: 'the built-in award shown here',
    order: 70 + index,
  })),
  ...PINGTOP_LEVEL_RANKS.map(([id, title, level, start, end, text], index) => ({
    productKey: `LIVE_BADGE_PLEVEL_${id.toUpperCase()}`,
    section: 'pingtop-level' as const,
    group: 'pingtop-level-ranks',
    kind: 'colors_only' as const,
    rankId: id,
    colors: true,
    defaultColors: { start, end, text },
    title,
    description: `Level ${level}+.`,
    previewLabel: String(level),
    fallback: 'the app colours',
    order: 100 + index,
  })),
];

/** Checks a file before upload. Returns an error message, or null when it is fine. */
export const checkLiveBadgeFile = (file: Pick<File, 'type' | 'size'>, kind: LiveBadgeKind = 'pill_icon'): string | null => {
  if (!LIVE_BADGE_CONTENT_TYPES.includes(file.type)) return 'Use a PNG or WebP image.';
  const max = LIVE_BADGE_MAX_BYTES[kind];
  if (!max) return 'This badge has no image here.';
  if (file.size > max) return `The image must be ${max / 1024} KB or smaller.`;
  return null;
};

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** Normalised `#RRGGBB`, or null when the text is not a 6-digit hex colour. */
export const normalizeHex = (value: string): string | null => {
  const trimmed = value.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return HEX.test(withHash) ? withHash.toUpperCase() : null;
};

/** Saved colours when all three are valid, else null. */
export const parsePillColors = (raw: unknown): PillColors | null => {
  if (!raw || typeof raw !== 'object') return null;
  const { start, end, text } = raw as Record<string, unknown>;
  const values = [start, end, text].map((value) => (typeof value === 'string' ? normalizeHex(value) : null));
  if (values.some((value) => !value)) return null;
  return { start: values[0]!, end: values[1]!, text: values[2]! };
};
